import React from "react";
import LegalLayout from "../components/site/LegalLayout";
import {
  Section,
  Callout,
  Pills,
  DocumentInfo,
  StepTimeline,
  BadgeTable,
  CardGrid,
  SupportCard,
  CompanyCard,
} from "../components/site/LegalBlocks";
import { COMPANY } from "../data/site";

const TOC = [
  { id: "introduction", title: "Introduction" },
  { id: "purpose", title: "Purpose and Objectives" },
  { id: "scope", title: "Scope and Applicability" },
  { id: "regulatory-framework", title: "Applicable Regulatory Framework" },
  { id: "eligibility", title: "Merchant Eligibility Criteria" },
  { id: "registration-process", title: "Merchant Registration Process" },
  { id: "kym", title: "Know Your Merchant (KYM) and Due Diligence" },
  { id: "risk-assessment", title: "Merchant Risk Assessment" },
  { id: "service-authorization", title: "Service-Specific Authorization" },
  { id: "approval-activation", title: "Merchant Approval and Activation" },
  { id: "wallet-controls", title: "Merchant Wallet and Transaction Controls" },
  { id: "code-of-conduct", title: "Merchant Responsibilities and Code of Conduct" },
  { id: "data-privacy", title: "Data Privacy and Information Security" },
  { id: "fraud-prevention", title: "Fraud Prevention and Financial Crime Controls" },
  { id: "ongoing-monitoring", title: "Ongoing Merchant Monitoring" },
  { id: "profile-updates", title: "Merchant Profile Updates and Re-Verification" },
  { id: "suspension-termination", title: "Suspension, Restriction, and Termination" },
  { id: "grievances", title: "Merchant Grievances and Appeals" },
  { id: "recordkeeping", title: "Recordkeeping and Audit Trail" },
  { id: "training", title: "Training and Operational Readiness" },
  { id: "partner-requirements", title: "Third-Party and Banking Partner Requirements" },
  { id: "fees", title: "Fees, Packages, and Commercial Terms" },
  { id: "roles", title: "Roles and Responsibilities" },
  { id: "enforcement", title: "Policy Violations and Enforcement" },
  { id: "amendments", title: "Policy Amendments" },
  { id: "governing-law", title: "Governing Law" },
  { id: "acceptance", title: "Policy Acceptance" },
  { id: "company-information", title: "Company Information" },
];

const DOCUMENT_INFO = [
  ["Document Title", "Merchant Onboarding Policy"],
  ["Version", "1.0"],
  ["Applicable Platform", "JayesPay Web and Mobile Applications"],
  ["Jurisdiction", "India"],
  ["Effective Date", "26 October 2026"],
  ["Document Status", "Active"],
];

const REGISTRATION_STEPS = [
  {
    title: "Account Registration",
    body: (
      <>
        The applicant shall provide the required basic details, which may
        include: full legal name; mobile number; email address; account
        credentials; business or shop name, if applicable; and state,
        district, and PIN code.
      </>
    ),
  },
  {
    title: "Mobile and Email Verification",
    body: (
      <>
        JayesPay may verify the applicant&apos;s registered mobile number and
        email address using OTP or another approved verification mechanism.
        Successful OTP verification confirms control of the relevant
        communication channel but does not, by itself, establish legal
        identity.
      </>
    ),
  },
  {
    title: "Merchant Profile Completion",
    body: "The applicant shall complete the required personal, business, address, and banking information.",
  },
  {
    title: "Identity and Business Verification",
    body: "Documents and information shall be collected and verified using legally permitted methods appropriate to the applicant and requested services.",
  },
  {
    title: "Risk Assessment",
    body: "The application shall undergo the applicable automated and/or manual risk evaluation.",
  },
  {
    title: "Merchant Approval",
    body: "The application shall be approved, rejected, or placed under review by an authorized process.",
  },
  {
    title: "Package Activation",
    body: (
      <>
        Where applicable, the approved merchant may select or activate an
        eligible JayesPay service package. A paid package shall not be
        represented as guaranteeing banking approval or access to restricted
        financial services.
      </>
    ),
  },
  {
    title: "Wallet and Service Activation",
    body: "The merchant may be permitted to fund an eligible wallet or transaction balance and access services authorized for that account.",
  },
];

const RISK_CATEGORIES = [
  {
    label: "Low Risk",
    tone: "bg-successBg text-successText",
    description:
      "Identity, business, and operational information is satisfactorily verified, with no material identified concerns.",
  },
  {
    label: "Medium Risk",
    tone: "bg-warnBg text-warnText",
    description:
      "Additional verification or monitoring is warranted due to operational characteristics or unresolved non-critical concerns.",
  },
  {
    label: "High Risk",
    tone: "bg-errorBg text-errorText",
    description:
      "Enhanced due diligence, senior approval, or partner review is required because of elevated fraud, financial crime, or operational exposure.",
  },
  {
    label: "Prohibited / Unacceptable",
    tone: "bg-ink900 text-surfaceAlt",
    description:
      "The activity is unlawful, prohibited by an applicable partner, or cannot be approved within the Company's risk appetite.",
  },
];

const ACCOUNT_STATES = [
  "Registered",
  "Verification Pending",
  "Under Review",
  "Additional Information Required",
  "Approved",
  "Restricted",
  "Suspended",
  "Rejected",
  "Terminated",
];

const ROLES = [
  {
    title: "Compliance and Risk Function",
    body: "Responsible for establishing risk controls, reviewing high-risk applications, overseeing monitoring procedures, and escalating relevant regulatory or financial crime concerns.",
  },
  {
    title: "Merchant Onboarding Team",
    body: "Responsible for application review, document completeness, verification coordination, and merchant communication.",
  },
  {
    title: "Operations Team",
    body: "Responsible for approved activation, operational support, settlement coordination, and service-related issues.",
  },
  {
    title: "Information Security Team",
    body: "Responsible for account protection, access security, incident response, and relevant technical safeguards.",
  },
  {
    title: "Management",
    body: "Responsible for policy oversight, authorization of material exceptions where legally permissible, adequate governance, and required corrective actions.",
  },
  {
    title: "Banking and Financial Partners",
    body: "Responsible for the regulatory, contractual, approval, monitoring, and supervisory activities assigned to them under applicable law and service arrangements.",
  },
];

export default function MerchantOnboardingPolicy() {
  return (
    <LegalLayout
      title="Merchant Onboarding Policy"
      eyebrow="Legal"
      description="How JayesPay verifies, approves, activates and monitors merchants and retailers on its platform."
      lastUpdated="26 October 2026"
      toc={TOC}
      testId="merchant-onboarding-page"
    >
      <DocumentInfo items={DOCUMENT_INFO} />

      <Section id="introduction" number={1} title="Introduction">
        <p>
          JayesPay is a retailer-first financial technology platform operated
          as a product of <strong>{COMPANY.legalName}</strong> (“Company”,
          “JayesPay”, “we”, “our”, or “us”).
        </p>
        <p>
          JayesPay enables eligible merchants and retailers to provide
          financial, banking-assisted, payment, recharge, bill payment, and
          other digital services to customers through its integrated platform.
        </p>
        <p>
          Considering the financial nature of these services, JayesPay is
          committed to maintaining a secure, transparent, and risk-based
          merchant onboarding process.
        </p>
        <p>
          This Merchant Onboarding Policy (“Policy”) establishes the
          eligibility requirements, documentation standards, identity and
          business verification procedures, risk assessment principles,
          approval processes, ongoing monitoring measures, and compliance
          obligations applicable to merchants seeking access to JayesPay.
        </p>
        <p>
          This Policy must be read alongside the applicable Merchant Terms and
          Conditions, <a href="/privacy-policy">Privacy Policy</a>,
          service-specific agreements, financial partner requirements, and
          operating procedures.
        </p>
        <p>
          Nothing in this Policy authorizes JayesPay or a merchant to conduct
          an activity that requires a regulatory authorization, licence,
          registration, appointment, or approval that has not been obtained.
        </p>
      </Section>

      <Section id="purpose" number={2} title="Purpose and Objectives">
        <p>The objectives of this Policy are to:</p>
        <ol>
          <li>Establish a consistent and transparent merchant onboarding framework.</li>
          <li>Verify the identity, legitimacy, and suitability of applicants.</li>
          <li>
            Prevent identity theft, fraudulent registrations, impersonation,
            and unauthorized platform access.
          </li>
          <li>
            Identify and manage risks relating to money laundering, terrorist
            financing, financial fraud, and other prohibited activities.
          </li>
          <li>Protect customers, merchants, banking partners, and the platform.</li>
          <li>
            Ensure that access to financial services is provided only after
            the applicable verification and authorization requirements have
            been satisfied.
          </li>
          <li>
            Define merchant obligations relating to customer data, financial
            transactions, confidentiality, and ethical business conduct.
          </li>
          <li>
            Establish procedures for ongoing monitoring, suspension,
            termination, and periodic re-verification.
          </li>
          <li>
            Support compliance with applicable Indian laws, regulations, and
            contractual obligations imposed by authorized financial service
            providers.
          </li>
        </ol>
      </Section>

      <Section id="scope" number={3} title="Scope and Applicability">
        <p>
          This Policy applies to all individuals, proprietors, firms,
          partnerships, companies, and other eligible business entities
          applying to become merchants or retailers on JayesPay.
        </p>
        <p>It covers:</p>
        <ul>
          <li>New merchant registration and verification.</li>
          <li>Existing merchant profile updates and re-verification.</li>
          <li>Business and identity due diligence.</li>
          <li>Risk classification and approval.</li>
          <li>Wallet and transaction service activation.</li>
          <li>Service-specific financial and banking authorization.</li>
          <li>Merchant operations and ongoing compliance monitoring.</li>
          <li>Suspension, restriction, and termination of merchant access.</li>
        </ul>
        <p>
          Where JayesPay operates as a technology or distribution partner for a
          regulated entity, the relevant regulated entity&apos;s requirements
          shall additionally apply.
        </p>
        <p>
          Where a partner&apos;s requirements are stricter than JayesPay&apos;s
          internal standards, the applicable stricter requirements shall
          govern access to that partner&apos;s services, subject to law.
        </p>
      </Section>

      <Section id="regulatory-framework" number={4} title="Applicable Regulatory Framework">
        <p>
          Merchant onboarding and operation shall be conducted with regard to
          applicable provisions of Indian law and relevant regulatory
          directions, including, where applicable:
        </p>
        <ul>
          <li>Reserve Bank of India Act, 1934.</li>
          <li>Banking Regulation Act, 1949.</li>
          <li>Payment and Settlement Systems Act, 2007.</li>
          <li>Prevention of Money Laundering Act, 2002, and applicable rules.</li>
          <li>RBI Know Your Customer (KYC) Directions, as amended.</li>
          <li>
            Applicable RBI directions governing Business Correspondents,
            payments, prepaid payment instruments, and outsourced financial
            services.
          </li>
          <li>
            Applicable NPCI operating, participation, security, and transaction
            requirements.
          </li>
          <li>
            Applicable UIDAI legislation, regulations, circulars, and
            authentication or e-KYC requirements.
          </li>
          <li>Information Technology Act, 2000, and applicable rules.</li>
          <li>
            Digital Personal Data Protection Act, 2023, and provisions and
            rules to the extent in force.
          </li>
          <li>
            Applicable cybersecurity, data security, consumer protection, and
            grievance redressal requirements.
          </li>
          <li>Applicable requirements of FIU-IND and other competent authorities.</li>
          <li>
            Applicable service-provider, sponsor-bank, acquiring-bank,
            payment-partner, and technology-partner agreements.
          </li>
        </ul>
        <Callout title="Regulatory applicability">
          Not every law or direction listed above necessarily applies directly
          to JayesPay or every merchant. The Company shall determine its actual
          obligations according to its legal role, regulated status, service
          arrangements, and the requirements of its banking and financial
          partners.
        </Callout>
      </Section>

      <Section id="eligibility" number={5} title="Merchant Eligibility Criteria">
        <h3>5.1 Minimum Eligibility Requirements</h3>
        <p>An applicant seeking merchant registration must:</p>
        <ol>
          <li>Be at least 18 years old if applying as an individual.</li>
          <li>Be legally competent to enter into a binding contract.</li>
          <li>
            Be an Indian resident or an entity legally permitted to operate the
            relevant business in India, subject to applicable service rules.
          </li>
          <li>Possess a valid, verifiable identity.</li>
          <li>Provide accurate and complete registration information.</li>
          <li>
            Operate a legitimate business or meet the eligibility criteria
            established for the intended merchant category.
          </li>
          <li>Have lawful access to the registered mobile number and email address.</li>
          <li>Provide acceptable business and banking information where required.</li>
          <li>
            Successfully complete applicable identity, business, risk, and
            service-specific verification procedures.
          </li>
          <li>Accept the applicable JayesPay agreements and operating conditions.</li>
        </ol>
        <p>
          <strong>Registration does not guarantee approval.</strong>
        </p>

        <h3>5.2 Eligible Business Categories</h3>
        <p>Subject to evaluation, applicants may include:</p>
        <ul>
          <li>Retail shops and general merchants.</li>
          <li>Mobile recharge and digital service outlets.</li>
          <li>Common service and assisted digital service operators.</li>
          <li>Authorized banking or financial service outlets.</li>
          <li>Travel and ticketing service outlets.</li>
          <li>Sole proprietorships, partnerships, LLPs, and incorporated businesses.</li>
          <li>Other lawful retail establishments approved by JayesPay.</li>
        </ul>
        <p>Additional eligibility conditions may apply for specific services.</p>

        <h3>5.3 Ineligible or Restricted Applicants</h3>
        <p>JayesPay may reject, restrict, or escalate applications involving:</p>
        <ul>
          <li>False, misleading, forged, or unverifiable information.</li>
          <li>Identity theft or unauthorized use of another person&apos;s documents.</li>
          <li>Prohibited or unlawful business activities.</li>
          <li>Materially suspicious or unexplained financial activity.</li>
          <li>Persons or entities subject to applicable legal prohibitions or sanctions.</li>
          <li>Previously terminated merchants attempting unauthorized re-registration.</li>
          <li>Unapproved agents acting on behalf of others.</li>
          <li>
            Businesses whose ownership, operations, or location cannot be
            satisfactorily verified.
          </li>
          <li>
            Applicants who fail to satisfy required risk or partner
            verification standards.
          </li>
        </ul>
        <p>
          Adverse information shall be assessed fairly and in context. Mere
          allegations, automated risk flags, or inconclusive screening results
          shall not automatically be treated as proof of wrongdoing.
        </p>
      </Section>

      <Section id="registration-process" number={6} title="Merchant Registration Process">
        <p>JayesPay shall implement a structured merchant registration process.</p>
        <StepTimeline steps={REGISTRATION_STEPS} />
        <Callout tone="warn">
          <strong>
            Merchant registration, merchant approval, wallet activation, and
            banking service authorization shall be treated as distinct events.
          </strong>
        </Callout>
      </Section>

      <Section id="kym" number={7} title="Know Your Merchant (KYM) and Due Diligence">
        <p>
          JayesPay shall adopt risk-based Know Your Merchant (“KYM”)
          procedures as an internal control.
        </p>
        <p>
          Where formal KYC or Customer Due Diligence obligations legally apply,
          those procedures shall be undertaken by the responsible regulated
          entity or authorized participant, in accordance with applicable
          requirements.
        </p>

        <h3>7.1 Individual Identity Information</h3>
        <p>
          Depending on the service and permitted verification method, the
          applicant may be required to provide:
        </p>
        <ul>
          <li>Full legal name.</li>
          <li>Date of birth.</li>
          <li>Recent photograph.</li>
          <li>PAN details.</li>
          <li>Acceptable proof of identity.</li>
          <li>Residential address.</li>
          <li>Acceptable proof of address.</li>
          <li>Mobile number and email address.</li>
        </ul>

        <h3>7.2 Aadhaar-Related Verification</h3>
        <p>
          Where Aadhaar-based verification is supported, it must be conducted
          only through legally permitted and properly authorized methods.
        </p>
        <p>JayesPay shall not:</p>
        <ul>
          <li>
            Treat Aadhaar submission as universally mandatory when lawful
            alternatives must be offered.
          </li>
          <li>Conduct unauthorized Aadhaar authentication or e-KYC.</li>
          <li>Store Aadhaar biometrics.</li>
          <li>
            Collect or retain Aadhaar numbers or authentication data beyond
            what is lawfully permitted.
          </li>
          <li>
            Use Aadhaar information for purposes unrelated to an authorized
            processing activity.
          </li>
        </ul>
        <p>
          Masked Aadhaar or other permitted verification alternatives should be
          used where appropriate.
        </p>
        <p>
          Access to Aadhaar-related information shall be restricted and handled
          according to applicable UIDAI and data protection requirements.
        </p>

        <h3>7.3 Business Verification</h3>
        <p>Depending on the business category, relevant documents may include:</p>
        <ul>
          <li>Business registration certificate.</li>
          <li>GST registration details, where applicable.</li>
          <li>Shop and Establishment registration, where applicable.</li>
          <li>Udyam registration, where available.</li>
          <li>Partnership deed.</li>
          <li>Certificate of incorporation.</li>
          <li>LLP documents.</li>
          <li>Proof of business premises.</li>
          <li>Applicable business licences or authorizations.</li>
          <li>Any additional evidence required by a service provider.</li>
        </ul>
        <p>
          A document not legally applicable to a particular business category
          shall not automatically be demanded from every merchant.
        </p>

        <h3>7.4 Authorized Representative and Beneficial Ownership</h3>
        <p>For non-individual applicants, JayesPay may verify:</p>
        <ul>
          <li>Legal existence of the entity.</li>
          <li>Authorized representatives.</li>
          <li>Directors or partners, as applicable.</li>
          <li>Ownership and control structure.</li>
          <li>Ultimate beneficial owners, where required.</li>
          <li>
            The representative&apos;s authority to enter into agreements and
            operate the account.
          </li>
        </ul>
        <p>
          Applicable ownership thresholds and verification rules shall be
          determined by the responsible compliance function and any relevant
          regulated partner.
        </p>

        <h3>7.5 Banking Information</h3>
        <p>Merchants may be required to provide:</p>
        <ul>
          <li>Account holder name.</li>
          <li>Bank account number.</li>
          <li>IFSC.</li>
          <li>Bank name.</li>
          <li>Permitted bank-account verification evidence.</li>
        </ul>
        <p>
          JayesPay may perform bank-account verification through approved
          service providers.
        </p>
        <p>
          Account ownership must be appropriately verified before permitting
          sensitive payouts or settlements.
        </p>
        <p>
          Third-party bank accounts shall not be accepted unless expressly
          supported by a lawful, documented, and approved use case.
        </p>
      </Section>

      <Section id="risk-assessment" number={8} title="Merchant Risk Assessment">
        <p>JayesPay shall maintain a documented merchant risk assessment framework.</p>

        <h3>8.1 Risk Classification</h3>
        <p>Applications may be classified internally as:</p>
        <BadgeTable rows={RISK_CATEGORIES} />

        <h3>8.2 Risk Factors</h3>
        <p>Risk assessments may consider:</p>
        <ul>
          <li>Identity and document inconsistencies.</li>
          <li>Ownership and business structure.</li>
          <li>Business type and transaction purpose.</li>
          <li>Requested financial services.</li>
          <li>Anticipated transaction volumes.</li>
          <li>Physical outlet or operating location.</li>
          <li>Adverse information and applicable screening results.</li>
          <li>Prior account restrictions and substantiated fraud history.</li>
          <li>Unusual device, network, or registration patterns.</li>
          <li>Service-provider risk assessments.</li>
        </ul>
        <p>
          Geography, business size, or automated signals shall not be used as
          arbitrary substitutes for meaningful due diligence.
        </p>

        <h3>8.3 Enhanced Due Diligence</h3>
        <p>Where justified, enhanced measures may include:</p>
        <ul>
          <li>Additional documentary verification.</li>
          <li>Video or physical verification, where appropriate and legally permitted.</li>
          <li>Business premises verification.</li>
          <li>Detailed review of ownership and control.</li>
          <li>Investigation of material inconsistencies.</li>
          <li>Additional banking-partner authorization.</li>
          <li>Senior compliance approval.</li>
          <li>Restricted transaction limits pending satisfactory review.</li>
          <li>Increased monitoring frequency.</li>
        </ul>
        <p>
          High-risk merchants shall not receive unrestricted service access
          solely through automated onboarding.
        </p>
      </Section>

      <Section id="service-authorization" number={9} title="Service-Specific Authorization">
        <p>
          Certain JayesPay services create greater financial, operational,
          regulatory, and fraud exposure than ordinary retail services.
        </p>
        <p>
          Access to these services shall be governed by additional
          eligibility, verification, partner authorization, and operational
          controls.
        </p>

        <h3>9.1 Recharge and Bill Payment Services</h3>
        <p>Approval may be subject to:</p>
        <ul>
          <li>Merchant identity verification.</li>
          <li>Applicable wallet or funding arrangements.</li>
          <li>Service-provider acceptance.</li>
          <li>Transaction limits.</li>
          <li>Fraud and misuse monitoring.</li>
          <li>Applicable BBPS or other payment-network requirements.</li>
        </ul>

        <h3>9.2 AePS and Aadhaar Pay</h3>
        <p>
          Where applicable, merchants must satisfy the requirements of the
          sponsoring or acquiring bank and relevant network participants.
        </p>
        <p>Additional controls may include:</p>
        <ul>
          <li>Service-specific outlet approval.</li>
          <li>Authorized operator verification.</li>
          <li>Approved biometric devices and integrations.</li>
          <li>Physical location validation, where required.</li>
          <li>Customer presence and authorization procedures.</li>
          <li>Cash availability and transaction reconciliation controls.</li>
          <li>Fraud, dispute, and failed-transaction handling.</li>
          <li>Applicable merchant transaction limits and monitoring.</li>
        </ul>
        <p>
          Customer biometrics must not be stored or reused by the merchant or
          JayesPay in an unauthorized manner.
        </p>

        <h3>9.3 Micro-ATM and Cash Services</h3>
        <p>Activation may require:</p>
        <ul>
          <li>Approval from the relevant sponsoring or acquiring institution.</li>
          <li>Authorized terminals or devices.</li>
          <li>Merchant location and operator verification.</li>
          <li>Cash-management procedures.</li>
          <li>Settlement and reconciliation controls.</li>
          <li>Customer receipt and transaction confirmation processes.</li>
          <li>Appropriate security and fraud-prevention measures.</li>
        </ul>

        <h3>9.4 Domestic Money Transfer</h3>
        <p>
          Merchants seeking DMT access shall comply with applicable
          remittance-provider and regulated-entity requirements, including
          relevant customer identification, transaction authorization, and
          limit controls.
        </p>
        <p>
          Merchants must not structure transactions to evade applicable
          thresholds or monitoring requirements.
        </p>

        <h3>9.5 Banking Correspondent Services</h3>
        <Callout tone="warn">
          <strong>
            A JayesPay merchant account does not automatically confer Business
            Correspondent status.
          </strong>
        </Callout>
        <p>
          A merchant may perform BC activities only after the necessary
          appointment, approval, authorization, and operational requirements
          have been satisfied through the relevant bank and contractual
          arrangement.
        </p>
        <p>Where required, BC onboarding may include:</p>
        <ul>
          <li>Additional merchant due diligence.</li>
          <li>Bank approval.</li>
          <li>Training and certification.</li>
          <li>Authorized outlet mapping.</li>
          <li>Permitted banking-device configuration.</li>
          <li>Cash handling and safeguarding arrangements.</li>
          <li>Required customer disclosures.</li>
          <li>Bank-prescribed monitoring and reporting.</li>
          <li>Applicable branding, signage, and grievance arrangements.</li>
        </ul>
        <p>
          Merchants shall not represent themselves as employees, branches, or
          authorized representatives of a bank beyond the scope of their actual
          appointment.
        </p>

        <h3>9.6 Insurance, Loans, and Credit Cards</h3>
        <p>
          Distribution, solicitation, referral, or facilitation of insurance
          and credit products shall occur only through legally permitted
          arrangements.
        </p>
        <p>Additional requirements may include:</p>
        <ul>
          <li>Partner approval.</li>
          <li>Applicable training and certification.</li>
          <li>Approved customer disclosures.</li>
          <li>Restricted marketing and solicitation practices.</li>
          <li>Appropriate consent before sharing application information.</li>
          <li>Prohibition of false approval guarantees.</li>
          <li>
            Prohibition of misleading claims relating to interest rates,
            coverage, benefits, or eligibility.
          </li>
        </ul>
        <p>
          Merchant access to one financial service does not establish authority
          to distribute every financial product.
        </p>

        <h3>9.7 Travel, PAN, and Other Assisted Services</h3>
        <p>
          Relevant partner terms, statutory requirements, service-provider
          guidelines, identity controls, pricing rules, and customer consent
          procedures shall apply.
        </p>
        <p>
          Where services require special registrations or authorizations, the
          merchant must obtain them before operating the service.
        </p>
      </Section>

      <Section id="approval-activation" number={10} title="Merchant Approval and Activation">
        <p>JayesPay shall maintain defined authorization controls for merchant approval.</p>
        <p>Merchant account states may include:</p>
        <Pills items={ACCOUNT_STATES} />
        <p>Individual financial services shall have independent activation states.</p>
        <p>
          Approvals shall be supported by appropriate records identifying the
          review outcome, authorization basis, and responsible approval
          process.
        </p>
        <p>
          High-risk applications and exceptional approvals shall follow
          designated escalation procedures.
        </p>
        <p>
          JayesPay reserves the right to reject or restrict applications based
          on documented legal, security, operational, risk, or partner
          requirements.
        </p>
      </Section>

      <Section id="wallet-controls" number={11} title="Merchant Wallet and Transaction Controls">
        <p>
          Merchant access to wallet balances, funding methods, transaction
          services, and payouts shall be subject to applicable product
          architecture, legal classification, and partner arrangements.
        </p>
        <p>Controls may include:</p>
        <ul>
          <li>Approved funding channels.</li>
          <li>Bank account verification.</li>
          <li>Transaction authentication.</li>
          <li>Service-specific balance and limit controls.</li>
          <li>T-PIN or other approved transaction security methods.</li>
          <li>Fraud monitoring.</li>
          <li>Transaction logging.</li>
          <li>Settlement and reconciliation.</li>
          <li>Refund and reversal procedures.</li>
          <li>Restrictions on unauthorized third-party funding or transfers.</li>
        </ul>
        <Callout tone="warn">
          <strong>
            JayesPay shall not describe any wallet balance as a bank deposit,
            insured deposit, or regulated prepaid payment instrument unless that
            characterization is legally accurate.
          </strong>
        </Callout>
        <p>
          The merchant must not use another person&apos;s credentials or
          transact on behalf of another merchant account without explicit
          authorization.
        </p>
      </Section>

      <Section id="code-of-conduct" number={12} title="Merchant Responsibilities and Code of Conduct">
        <p>Approved merchants shall:</p>
        <ol>
          <li>Provide accurate information and keep it updated.</li>
          <li>Operate only approved services.</li>
          <li>
            Follow the applicable operational instructions of JayesPay and its
            partners.
          </li>
          <li>Display or communicate applicable service charges to customers.</li>
          <li>Obtain required customer authorization before initiating transactions.</li>
          <li>Issue receipts or acknowledgments where required.</li>
          <li>Protect customer information and transaction credentials.</li>
          <li>Maintain confidentiality of account access information.</li>
          <li>
            Report suspected fraud, unauthorized access, and security incidents
            promptly.
          </li>
          <li>Cooperate with lawful verification, investigation, and audit requests.</li>
          <li>
            Maintain adequate cash and transaction reconciliation practices
            where relevant.
          </li>
          <li>
            Refrain from misleading customers about banking affiliations or
            financial products.
          </li>
        </ol>

        <h3>Prohibited Merchant Conduct</h3>
        <p>Merchants must not:</p>
        <ul>
          <li>Initiate fraudulent, fictitious, or unauthorized transactions.</li>
          <li>Split or structure transactions to circumvent limits or controls.</li>
          <li>Charge unauthorized or undisclosed fees.</li>
          <li>
            Retain customer OTPs, PINs, passwords, or sensitive authentication
            credentials.
          </li>
          <li>Store customer biometric data.</li>
          <li>Use another merchant&apos;s identity or login.</li>
          <li>Share their account with unauthorized operators.</li>
          <li>Misrepresent service eligibility or approvals.</li>
          <li>Submit forged documentation.</li>
          <li>Manipulate reversals, refunds, or transaction records.</li>
          <li>
            Conduct unauthorized cash withdrawal, deposit, transfer, or banking
            activities.
          </li>
          <li>Engage in unlawful money movement or customer exploitation.</li>
        </ul>
        <p>
          Violations may result in restrictions, suspension, termination,
          recovery actions, or reporting to the appropriate partner or
          authority where justified or required.
        </p>
      </Section>

      <Section id="data-privacy" number={13} title="Data Privacy and Information Security">
        <p>
          JayesPay shall implement appropriate technical and organizational
          safeguards to protect merchant and customer information.
        </p>
        <p>Measures should include:</p>
        <ul>
          <li>Secure transmission of sensitive information.</li>
          <li>Encryption where appropriate.</li>
          <li>Access control based on job responsibilities.</li>
          <li>Authentication and session security.</li>
          <li>Security monitoring and access logging.</li>
          <li>Controlled access to verification records.</li>
          <li>Secure document storage.</li>
          <li>Retention and deletion controls.</li>
          <li>Vendor and third-party data handling safeguards.</li>
          <li>Incident response and remediation procedures.</li>
        </ul>
        <p>
          Personal data shall be processed only for lawful, disclosed purposes
          with the required legal basis and notices.
        </p>
        <p>
          Merchant records shall not be retained indefinitely merely because
          they were collected during onboarding.
        </p>
        <p>
          Relevant information may be preserved for the periods required by
          applicable law, legitimate contractual obligations, dispute handling,
          or regulatory and audit requirements.
        </p>
        <p>
          Biometric authentication information must be processed strictly
          within the authorization and security requirements applicable to the
          relevant service.
        </p>
      </Section>

      <Section id="fraud-prevention" number={14} title="Fraud Prevention and Financial Crime Controls">
        <p>
          JayesPay shall implement risk-based fraud prevention measures
          proportional to its operational role.
        </p>
        <p>These may include:</p>
        <ul>
          <li>Duplicate application detection.</li>
          <li>Document integrity checks.</li>
          <li>Suspicious registration pattern detection.</li>
          <li>Device and session security controls.</li>
          <li>Transaction velocity monitoring.</li>
          <li>Unusual activity detection.</li>
          <li>Applicable sanctions and watchlist screening.</li>
          <li>Fraud investigation and escalation.</li>
          <li>Partner notification.</li>
          <li>Temporary restrictions on suspicious activity.</li>
          <li>Preservation of relevant evidence.</li>
        </ul>
        <p>
          Where suspicious transaction reporting or other financial
          intelligence obligations apply, the legally responsible reporting
          entity shall perform those obligations.
        </p>
        <p>
          JayesPay shall support authorized partners in fulfilling their duties
          as required by law and contractual arrangements.
        </p>
        <p>
          Merchants shall not be informed of a confidential investigation or
          report where disclosure is prohibited by law.
        </p>
      </Section>

      <Section id="ongoing-monitoring" number={15} title="Ongoing Merchant Monitoring">
        <p>Onboarding approval is not a permanent exemption from review.</p>
        <p>JayesPay may conduct ongoing verification and monitoring based on:</p>
        <ul>
          <li>Merchant risk classification.</li>
          <li>Service usage.</li>
          <li>Transaction patterns.</li>
          <li>Fraud alerts.</li>
          <li>Changes in ownership or control.</li>
          <li>Changes in business location.</li>
          <li>Identity or documentation changes.</li>
          <li>Partner instructions.</li>
          <li>Security incidents.</li>
          <li>Regulatory requirements.</li>
        </ul>
        <p>
          The frequency and depth of review shall be defined through internal
          risk procedures and applicable partner standards.
        </p>
        <p>
          A merchant may be required to complete re-verification before
          certain services remain available.
        </p>
      </Section>

      <Section id="profile-updates" number={16} title="Merchant Profile Updates and Re-Verification">
        <p>
          Merchants shall notify JayesPay of material changes to their
          identity, ownership, business details, registered contact
          information, or banking arrangements.
        </p>
        <p>JayesPay may require additional verification when:</p>
        <ul>
          <li>A registered bank account is changed.</li>
          <li>A business owner or authorized operator changes.</li>
          <li>A material business location change occurs.</li>
          <li>Significant identity discrepancies are detected.</li>
          <li>A previously verified document becomes invalid.</li>
          <li>A banking or financial partner requests re-verification.</li>
          <li>
            A security incident creates reasonable doubts about account
            ownership.
          </li>
        </ul>
        <p>
          Sensitive account changes may be subject to temporary transaction
          restrictions until verified.
        </p>
      </Section>

      <Section id="suspension-termination" number={17} title="Suspension, Restriction, and Termination">
        <p>
          JayesPay may suspend or restrict an account or specific service where
          reasonably necessary due to:
        </p>
        <ul>
          <li>Suspected unauthorized activity.</li>
          <li>Material KYC or KYM discrepancies.</li>
          <li>Confirmed fraudulent conduct.</li>
          <li>Breach of contractual or operational requirements.</li>
          <li>Security threats.</li>
          <li>Regulatory or lawful authority directions.</li>
          <li>Service-provider or partner restrictions.</li>
          <li>Failure to complete required re-verification.</li>
          <li>Misuse of banking or financial service access.</li>
        </ul>
        <p>
          Where permitted and appropriate, the merchant shall receive
          notification and an opportunity to provide clarification.
        </p>
        <p>
          JayesPay may take immediate protective action where necessary to
          prevent financial loss, protect customers, comply with law, or meet
          partner instructions.
        </p>
        <p>
          Termination decisions shall be documented and subject to appropriate
          internal controls.
        </p>
        <p>
          Any remaining balances, pending settlements, refunds, recovery
          obligations, or disputed funds shall be handled under the applicable
          legal and contractual arrangements.
        </p>
        <p>
          Termination shall not automatically entitle the Company to confiscate
          legitimate merchant balances.
        </p>
      </Section>

      <Section id="grievances" number={18} title="Merchant Grievances and Appeals">
        <p>Merchants may submit complaints concerning:</p>
        <ul>
          <li>Onboarding delays.</li>
          <li>Rejected applications.</li>
          <li>Verification discrepancies.</li>
          <li>Restricted accounts.</li>
          <li>Service activation.</li>
          <li>Wallet or settlement issues.</li>
          <li>Unauthorized transactions.</li>
          <li>Incorrect account information.</li>
        </ul>
        <p>
          JayesPay shall provide accessible support and an appropriate
          escalation mechanism.
        </p>
        <p>
          Complaints shall be acknowledged, investigated, and resolved
          according to the applicable grievance redressal procedures and
          legally prescribed timelines, where any apply.
        </p>
        <p>
          Where a regulated partner is responsible for a particular service,
          relevant complaints shall be escalated to that entity as required.
        </p>
        <p>
          Merchants shall not be denied access to applicable statutory or
          regulatory complaint mechanisms.
        </p>
        <SupportCard />
      </Section>

      <Section id="recordkeeping" number={19} title="Recordkeeping and Audit Trail">
        <p>
          JayesPay shall maintain appropriate onboarding, approval, and
          operational records, including where relevant:
        </p>
        <ul>
          <li>Registration details.</li>
          <li>Verification outcomes.</li>
          <li>Submitted documents.</li>
          <li>Merchant agreements and acceptance records.</li>
          <li>Risk assessment results.</li>
          <li>Approval decisions.</li>
          <li>Service activation records.</li>
          <li>Significant account changes.</li>
          <li>Investigation and restriction records.</li>
          <li>Relevant communication and grievance history.</li>
        </ul>
        <p>
          Records shall be protected against unauthorized access, alteration,
          and deletion.
        </p>
        <p>
          Retention periods shall be determined by applicable legal
          obligations, the Company&apos;s role, contractual requirements, and
          approved retention schedules.
        </p>
        <p>
          Authorized audits and compliance reviews may be conducted to evaluate
          adherence to this Policy.
        </p>
      </Section>

      <Section id="training" number={20} title="Training and Operational Readiness">
        <p>
          Where required by a service provider or internal risk assessment,
          merchants may be required to complete onboarding training before
          service activation.
        </p>
        <p>Training may cover:</p>
        <ul>
          <li>Customer identity and authorization.</li>
          <li>Fraud awareness.</li>
          <li>Transaction processing.</li>
          <li>Customer information security.</li>
          <li>Cash handling.</li>
          <li>Failed transactions and reversals.</li>
          <li>Customer complaint handling.</li>
          <li>Applicable financial service restrictions.</li>
          <li>Device and account security.</li>
        </ul>
        <p>
          Certain services may require certification or formal approval from
          the relevant institution.
        </p>
      </Section>

      <Section id="partner-requirements" number={21} title="Third-Party and Banking Partner Requirements">
        <p>
          JayesPay works with service providers, financial institutions, and
          technology partners to deliver eligible services.
        </p>
        <p>
          Access to partner services may depend on independent verification,
          technical eligibility, contractual approval, and regulatory
          requirements.
        </p>
        <p>
          JayesPay may share necessary merchant information with authorized
          partners in accordance with applicable privacy and data protection
          requirements.
        </p>
        <p>
          Approval on JayesPay does not guarantee approval by any bank, payment
          network, insurer, lender, or other financial institution.
        </p>
        <p>Each partner may impose additional lawful operating conditions.</p>
      </Section>

      <Section id="fees" number={22} title="Fees, Packages, and Commercial Terms">
        <p>
          Merchant onboarding, package activation, commissions, service
          charges, and applicable commercial conditions shall be governed by
          the relevant published terms or merchant agreement.
        </p>
        <p>Merchants must be informed of applicable fees before they become payable.</p>
        <p>
          Where a package is purchased but onboarding or a particular service
          authorization is rejected, any refund or adjustment shall be
          determined by the applicable disclosed terms, consumer protection
          obligations, and other relevant law. See our{" "}
          <a href="/refund-policy">Refund Policy</a>.
        </p>
        <p>Commission eligibility does not authorize fraudulent or artificial transactions.</p>
        <p>
          JayesPay may investigate and adjust commissions associated with
          invalid or reversed transactions according to applicable contractual
          terms.
        </p>
      </Section>

      <Section id="roles" number={23} title="Roles and Responsibilities">
        <CardGrid items={ROLES} />
      </Section>

      <Section id="enforcement" number={24} title="Policy Violations and Enforcement">
        <p>Any suspected violation may be assessed through internal review.</p>
        <p>
          Depending on the nature and severity of the issue, measures may
          include:
        </p>
        <ul>
          <li>Warning or corrective instructions.</li>
          <li>Additional verification.</li>
          <li>Transaction limit restrictions.</li>
          <li>Service deactivation.</li>
          <li>Temporary suspension.</li>
          <li>Merchant termination.</li>
          <li>Recovery or dispute proceedings where legally justified.</li>
          <li>Referral to relevant partners or competent authorities.</li>
        </ul>
        <p>
          Actions shall be proportionate to the identified risk, applicable
          law, contractual obligations, and available evidence.
        </p>
      </Section>

      <Section id="amendments" number={25} title="Policy Amendments">
        <p>JayesPay reserves the right to amend this Policy in response to:</p>
        <ul>
          <li>Changes in applicable law or regulatory requirements.</li>
          <li>New banking or financial service integrations.</li>
          <li>Service-provider requirements.</li>
          <li>Changes in the Company&apos;s operating model.</li>
          <li>Fraud and cybersecurity risks.</li>
          <li>Internal control improvements.</li>
        </ul>
        <p>
          Material changes shall be communicated as required by applicable law
          and contractual arrangements.
        </p>
      </Section>

      <Section id="governing-law" number={26} title="Governing Law">
        <p>This Policy shall be governed by applicable laws of India.</p>
        <p>
          Disputes shall be addressed under the governing law and dispute
          resolution provisions contained in the applicable merchant agreement,
          without limiting mandatory statutory or regulatory rights.
        </p>
      </Section>

      <Section id="acceptance" number={27} title="Policy Acceptance">
        <p>
          By applying for registration, merchants acknowledge that they must
          provide accurate information and complete applicable verification
          procedures.
        </p>
        <p>
          Where required, merchants shall separately accept the relevant
          contractual terms, service-specific agreements, data processing
          notices, and other binding documents.
        </p>
        <p>
          Merely viewing this Policy does not constitute consent to unrelated
          personal data processing or acceptance of an undisclosed financial
          obligation.
        </p>
      </Section>

      <Section id="company-information" number={28} title="Company Information">
        <CompanyCard />
      </Section>
    </LegalLayout>
  );
}
